import React from "react";
import RequestContractorDemoForm from "../components/demo/RequestContractorDemoForm";
import PublicNavBar from "../components/layout/PublicNavBar";
import { Card, CardContent } from "@/components/ui/card";
import {
  FileText,
  CreditCard,
  Users,
  Clock,
  UserCheck,
  BadgeCheck,
  Building2,
  Receipt,
  Shield,
} from "lucide-react";

const RequestContractorDemoPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <PublicNavBar />

      {/* Hero Section */}
      <div className="bg-secondary py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
                Try{" "}
                <span className="font-extrabold font-archivo text-primary">
                  Hirelo
                </span>{" "}
                for Contractors
              </h1>
              <p className="text-xl text-secondary-foreground/90 mb-6">
                The complete contractor management platform designed
                specifically for childcare professionals. Experience our
                contractor features with pre-configured demo accounts for both
                UK and US markets.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <div className="flex items-center gap-2 text-secondary-foreground">
                  <BadgeCheck className="h-6 w-6 text-primary" />
                  <span>HMRC Compliant</span>
                </div>
                <div className="flex items-center gap-2 text-secondary-foreground">
                  <Building2 className="h-6 w-6 text-primary" />
                  <span>Built for Contractors</span>
                </div>
                <div className="flex items-center gap-2 text-secondary-foreground">
                  <Clock className="h-6 w-6 text-primary" />
                  <span>Setup in Minutes</span>
                </div>
              </div>
            </div>
            <div>
              <Card className="shadow-lg">
                <CardContent className="pt-6">
                  <RequestContractorDemoForm />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Discover How Easy Contractor Management Can Be
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Receipt className="h-8 w-8" />}
              title="Professional Invoicing"
              description="Create and send professional invoices to your clients with automatic approval workflows."
            />
            <FeatureCard
              icon={<FileText className="h-8 w-8" />}
              title="Contract Management"
              description="Generate legally compliant contractor agreements with our built-in contract tool created by experts."
            />
            <FeatureCard
              icon={<Users className="h-8 w-8" />}
              title="Client Portal"
              description="Give your clients access to view and approve invoices through their own dedicated portal."
            />
            <FeatureCard
              icon={<Shield className="h-8 w-8" />}
              title="Business Setup"
              description="Complete contractor entity setup with business registration and VAT handling."
            />
            <FeatureCard
              icon={<CreditCard className="h-8 w-8" />}
              title="Payment Tracking"
              description="Track invoice payments and manage your contractor finances with ease."
            />
            <FeatureCard
              icon={<UserCheck className="h-8 w-8" />}
              title="Compliance Ready"
              description="Stay compliant with country-specific contractor regulations and tax requirements (HMRC for UK, IRS for US)."
            />
          </div>
        </div>
      </div>

      {/* What's Included Section */}
      <div className="py-16 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            What's Included in Your Country-Specific Contractor Demo
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
              <h3 className="text-2xl font-bold mb-4 text-primary">
                Contractor Account
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>
                    Complete contractor entity setup with business details
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>
                    Pre-configured contractor agreement and active contract
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Sample invoice already created and submitted</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Invoice creation and management tools</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-100">
              <h3 className="text-2xl font-bold mb-4 text-primary">
                Client Account
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Client portal to view contractor invoices</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Invoice approval and rejection workflows</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Contract viewing and management</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Client dashboard with payment overview</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Section */}
      <div className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            What Contractors Say About Hirelo
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <TestimonialCard
              quote="Hirelo made setting up my contractor business so much easier. The invoicing system is professional and my clients love the portal."
              author="Emma Johnson"
              title="Childcare Contractor"
            />
            <TestimonialCard
              quote="The contract generation tool saved me hundreds in legal fees. Everything is compliant and professional from day one."
              author="Sarah Williams"
              title="Nanny Contractor"
            />
            <TestimonialCard
              quote="My clients can approve invoices instantly through their portal. It's streamlined my entire billing process."
              author="Michael Chen"
              title="Childcare Professional"
            />
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-12 px-4 bg-background text-center">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Try Hirelo for Contractors?
          </h2>
          <p className="text-lg mb-8">
            Select your country and fill out the form above to get instant access to our 
            contractor demo environment with sample contractor and client accounts 
            configured for your region.
          </p>
        </div>
      </div>
    </div>
  );
};

// CheckCircle component for the included features
const CheckCircle: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M9 12l2 2 4-4" />
    <circle cx="12" cy="12" r="10" />
  </svg>
);

// Feature Card Component
const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
      <div className="text-primary mb-4">{icon}</div>
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
};

// Testimonial Card Component
const TestimonialCard: React.FC<{
  quote: string;
  author: string;
  title: string;
}> = ({ quote, author, title }) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100">
      <div className="text-primary mb-4">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-quote"
        >
          <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
          <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />
        </svg>
      </div>
      <p className="text-gray-700 italic mb-4">{quote}</p>
      <div className="mt-4">
        <p className="font-bold">{author}</p>
        <p className="text-sm text-gray-500">{title}</p>
      </div>
    </div>
  );
};

export default RequestContractorDemoPage;
